import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { Observable, of, EMPTY } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap, debounceTime } from 'rxjs/operators';

import { CareService } from '../care/care.service';
import { CareConstants } from '../care/care.constants';
import * as fromRoot from '../reducers';
import * as care from '../actions/care';
import * as user from '../actions/user';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ServiceNowReportComponent } from '../care/service-now-report/service-now-report.component';
import { ConfirmationDialogComponent } from '../shared/confirmation-dialog/confirmation-dialog.component';

import { ServiceNowCase } from '../models/recover/ServiceNowCase';
import { ReassignPayload } from '../models/recover/ReassignPayload';

@Injectable()
export class CareEffects {
  public getReports$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(
      care.ActionTypes.ACKNOWLEDGE_REPORT_SUCCESS,
      care.ActionTypes.CARE_CHANGE_VIEW,
      care.ActionTypes.CLOSED_CARE_CHANGE_VIEW,
      care.ActionTypes.LOAD_CARES_PAGE,
      care.ActionTypes.REASSIGN_REPORT_SUCCESS,
      care.ActionTypes.REOPEN_REPORT_SUCCESS,
    ),
    filter(() => this.router.url.includes('/care')),
    switchMap(() => this.store.select(fromRoot.getCasesRequest).pipe(take(1))),
    switchMap((requestObj) => this.cs.getCareReports(requestObj).pipe(
      map((res) => new care.GetCareReportsSuccess(res)),
      catchError((error) => of(new care.GetCareReportsError(error)))
    ))
  )});

  public acknowledgeReport$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(care.ActionTypes.ACKNOWLEDGE_REPORT),
    switchMap(() => this.store.select(fromRoot.getCareServiceNowCase).pipe(take(1))),
    switchMap((report: ServiceNowCase) => this.cs.acknowledgeReport(report).pipe(
      map((res) => new care.AcknowledgeReportSuccess(res)),
      catchError((error) => of(new care.AcknowledgeReportError(error)))
    ))
  )});

  public acknowledgeReportError$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(care.ActionTypes.ACKNOWLEDGE_REPORT_ERROR),
    map((action: any) => action.payload),
    tap((payload) => {
      this.router.navigate(['/care']);
      return this.snackBar.open(
        payload,
        'Dismiss', {
          duration: 5000
        }
      );
    })
  )}, { dispatch : false });

  public acknowledgeReportSuccess$: Observable<any> = createEffect(() => { return this.actions$.pipe(
    ofType(care.ActionTypes.ACKNOWLEDGE_REPORT_SUCCESS),
    switchMap(() => this.store.select(fromRoot.getCareServiceNowCase).pipe(take(1))),
    map((serviceNowCase) => {
      let config = new MatDialogConfig();
      config.data = {
        confirmText: 'Back',
        displayText: CareConstants.acknowledgeReportDialogText,
        hideCancel: true
      };
      this.dialog.open(ConfirmationDialogComponent, config).afterClosed().subscribe(() => {
        this.store.dispatch(new care.GetServiceNowReportDetails(serviceNowCase.id));
      });
    })
  )}, { dispatch : false });

  public reassignReport$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType<care.ReassignReport>(care.ActionTypes.REASSIGN_REPORT),
      switchMap((action) =>
        this.store.select(fromRoot.getCareServiceNowCaseForReassign).pipe(
          take(1),
          switchMap((reportFromStore) => {
            const finalPayload = {
              ...reportFromStore,
              ...action.payload
            } as ReassignPayload;

            if (finalPayload?.reassignUrl) {
              return EMPTY;
            }

            return this.cs.reassignReport(finalPayload).pipe(
              map((res) => new care.ReassignReportSuccess(res)),
              catchError((error) => of(new care.ReassignReportError(error)))
            );
          })
        )
      )
    );
  });

  public reassignReportForServiceCheck$: Observable<any> = createEffect(() => { return this.actions$.pipe(
    ofType(care.ActionTypes.REASSIGN_REPORT),
    switchMap(() => this.store.select(fromRoot.getCareServiceNowCase).pipe(take(1))),
    filter((report) => report && report.reassignUrl !== null),
    map((report) => {
      window.open(report.reassignUrl, '_blank');
    }),
    map((res) => new care.ReassignReportSuccess(res))
  )}, { dispatch : false });

  public reassignReportError$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(care.ActionTypes.REASSIGN_REPORT_ERROR),
    map((action: any) => action.payload),
    tap((payload) => {
      this.router.navigate(['/care']);
      return this.snackBar.open(
        payload,
        'Dismiss', {
          duration: 5000
        }
      );
    })
  )}, { dispatch : false });

  public reassignReportSuccess$: Observable<any> = createEffect(() => { return this.actions$.pipe(
    ofType(care.ActionTypes.REASSIGN_REPORT_SUCCESS),
    map(() => {
      let config = new MatDialogConfig();
      config.data = {
        confirmText: 'Back',
        displayText: CareConstants.reassignReportDialogText,
        hideCancel: true
      };
      config.panelClass = 'dialog-responsive'
      this.dialog.open(ConfirmationDialogComponent, config).afterClosed().subscribe((confirm) => {
        if (confirm) {
          this.router.navigate(['/care']);
        }
      });
    })
  )}, { dispatch : false });

  public getReportDetails$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(
      care.ActionTypes.GET_CARE_REPORT_DETAILS,
      care.ActionTypes.REOPEN_REPORT_SUCCESS
    ),
    map((action: any) => action.payload),
    switchMap((id) => this.cs.getReportDetails(id).pipe(
      map((res) => res != null ?
        new care.LoadCaseDetailsSuccess(res) :
        new care.GetCareReportDetailsError('There was an error retrieving care tickets details. Please try again later.')),
      catchError((error) => of(new care.GetCareReportDetailsError(error)))
    ))
  )});

  public getReportDetailsAfterStatusUpdate$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(care.ActionTypes.UPDATE_STATUS_SUCCESS),
    map((action: any) => action.payload),
    switchMap((payload) => this.cs.getReportDetails(payload.incidentId).pipe(
      map((res) => res != null ?
        new care.LoadCaseDetailsSuccess(res) :
        new care.GetCareReportDetailsError('There was an error retrieving care tickets details. Please try again later.')),
      catchError((error) => of(new care.GetCareReportDetailsError(error)))
    ))
  )});

  public getCareStatusOptions$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(care.ActionTypes.GET_CARE_STATUS_OPTIONS),
    switchMap(() => this.cs.getCareStatusOptions().pipe(
      map((res) => new care.GetCareStatusOptionsSuccess(res)),
      catchError((error) => of(new care.GetCareStatusOptionsError(error)))
    ))
  )});

  public getReportActivityHistory$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(
      care.ActionTypes.SELECT_REPORT,
      care.ActionTypes.UPDATE_STATUS_SUCCESS,
      care.ActionTypes.LOAD_CASE_DETAILS_SUCCESS,
      care.ActionTypes.REOPEN_REPORT_SUCCESS
    ),
    switchMap(() => this.store.select(fromRoot.getSelectedCareReport).pipe(take(1))),
    switchMap((report) => this.cs.getReportActivityHistory(report).pipe(
      map((res) => new care.GetActivityHistorySuccess(res)),
      catchError((error) => of(new care.GetActivityHistoryError(error)))
    ))
  )});

  public getCareEmailDetails$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(care.ActionTypes.GET_CARE_EMAIL_DETAILS),
    map((action: any) => action.payload),
    switchMap((id) => this.cs.getCareEmailDetails(id).pipe(
      map((res) => new care.GetCareEmailDetailsSuccess(res)),
      catchError((error) => of(new care.GetCareEmailDetailsError(error)))
    ))
  )});

  public getTemplate$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(care.ActionTypes.SELECT_TEMPLATE),
    map((action: any) => action.payload),
    filter((payload) => payload.memberType !== CareConstants.spotCustomer),
    switchMap(() => this.store.select(fromRoot.getCareTemplateRequest).pipe(take(1))),
    switchMap((request) => this.cs.getCareTemplate(request).pipe(
      map((res) => new care.FetchTemplateSuccess(res)),
      catchError((error) => of(new care.FetchTemplateError(error)))
    ))
  )});

  public getSpotMemberTemplate$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(care.ActionTypes.SELECT_TEMPLATE),
    map((action: any) => action.payload),
    filter((payload) => payload.memberType === CareConstants.spotCustomer),
    switchMap(() => this.store.select(fromRoot.getSpotCustomerCareTemplateRequest).pipe(take(1))),
    switchMap((type) => this.cs.getSpotCustomerCareTemplate(type).pipe(
      map((res) => new care.FetchTemplateSuccess(res)),
      catchError((error) => of(new care.FetchTemplateError(error)))
    ))
  )});

  public setIncidentStatus$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(care.ActionTypes.UPDATE_STATUS),
    map((action: any) => action.payload),
    switchMap((request) => this.cs.updateIncident(request).pipe(
      map((res) => new care.UpdateStatusSuccess(res)),
      catchError((error) => of(new care.UpdateStatusError(error)))
    ))
  )});

  public sendResponse$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(care.ActionTypes.SEND_RESPONSE),
    switchMap(() => this.store.select(fromRoot.getMemberType).pipe(take(1))),
    filter((memberType) => memberType !== CareConstants.spotCustomer),
    switchMap(() => this.store.select(fromRoot.getCareRespondPayload).pipe(take(1))),
    switchMap((payload) => this.cs.sendCareResponse(payload).pipe(
      map((incidentId) => {
        return new care.SendResponseSuccess({
          id: incidentId,
          message: CareConstants.sendSuccessMessage
        });
      }),
      catchError((incidentId) => {
        return of(new care.SendResponseError({
          incidentId,
          message: CareConstants.sendErrorMessage
        }));
      })
    ))
  )});

  public sendSpotResponse$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(care.ActionTypes.SEND_RESPONSE),
    switchMap(() => this.store.select(fromRoot.getMemberType).pipe(take(1))),
    filter((memberType) => memberType === CareConstants.spotCustomer),
    switchMap(() => this.store.select(fromRoot.getCareSpotRespondPayload).pipe(take(1))),
    switchMap((payload) => this.cs.sendSpotCareResponse(payload).pipe(
      map((response) => {
        return new care.SendResponseSuccess({
          ...response,
          message: CareConstants.sendSuccessMessage
        });
      }),
      catchError((response) => {
        return of(new care.SendResponseError({
          ...response,
          message: CareConstants.sendErrorMessage
        }));
      })
    ))
  )});

  public reopenReport$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(care.ActionTypes.REOPEN_REPORT),
    switchMap(() => this.store.select(fromRoot.getSelectedCareReport).pipe(take(1))),
    switchMap((report) => this.cs.reopenReport(report).pipe(
      map(() => new care.ReopenReportSuccess(report.id)),
      catchError((error) => of(new care.ReopenReportError(error)))
    ))
  )});

  public showCreateToast$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(care.ActionTypes.SEND_RESPONSE_SUCCESS),
    map((action: any) => action.payload),
    tap((payload) => {
      const { message } = payload;

      return this.snackBar.open(message, 'Dismiss', { duration: 5000 });
    })
  )}, { dispatch : false });

  public showCreateFailToast$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(care.ActionTypes.SEND_RESPONSE_ERROR),
    map((action: any) => action.payload),
    tap((payload) => {
      const { message } = payload;
      this.router.navigate(['/care']);
      return this.snackBar.open(
        message,
        'Dismiss', {
          duration: 5000
        }
      );
    })
  )}, { dispatch : false });

  public showReopenSuccessToast$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(care.ActionTypes.REOPEN_REPORT_SUCCESS),
    switchMap(() => this.store.select(fromRoot.getSelectedCareReportFullName).pipe(take(1))),
    tap((name) => {
      return this.snackBar.open(
        `CARES Contact for ${name} successfully reopened and moved to Spotlight Recover tab.`,
        'Dismiss', {
          duration: 5000
        }
      );
    })
  )}, { dispatch : false });

  public showReopenErrorToast$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(care.ActionTypes.REOPEN_REPORT_ERROR),
    switchMap(() => this.store.select(fromRoot.getSelectedCareReportFullName).pipe(take(1))),
    tap((name) => {
      return this.snackBar.open(
        `CARES Contact for ${name} failed to be reopened.`,
        'Dismiss', {
          duration: 5000
        }
      );
    })
  )}, { dispatch : false });

  public openServiceNowReport$: Observable<any> = createEffect(() => { return this.actions$.pipe(
    ofType(care.ActionTypes.OPEN_SERVICE_NOW_REPORT),
    switchMap((report: any) => this.cs.getCaseDetails(report.payload).pipe(
      map((res) => {
        const config: MatDialogConfig = { maxWidth: '1500px', maxHeight: '100vh' };
        this.dialog.open(ServiceNowReportComponent, config);
        return new care.LoadCaseDetailsSuccess((res))
      }),
      catchError((error) => of(new care.OpenServiceNowReportError(error)))
    ))
  )});

  public getServiceNowReportDetails$: Observable<any> = createEffect(() => { return this.actions$.pipe(
    ofType(care.ActionTypes.GET_SERVICE_NOW_REPORT_DETAILS),
    switchMap((report: any) => this.cs.getCaseDetails(report.payload).pipe(
      map((res) => new care.LoadCaseDetailsSuccess((res))),
      catchError((error) => of(new care.OpenServiceNowReportError(error)))
    ))
  )});

  public findSpotlightContactsDebounce$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(
      care.ActionTypes.SPOTLIGHT_CONTACTS_SEARCH,
      care.ActionTypes.SPOTLIGHT_CONTACTS_CHANGE_PAGE_SIZE,
      care.ActionTypes.SPOTLIGHT_CONTACTS_CHANGE_PAGE,
      care.ActionTypes.SPOTLIGHT_CONTACTS_CHANGE_SORT,
      care.ActionTypes.CFA_ONE_CHANGE_VIEW
    ),
    debounceTime(200),
    switchMap(() => this.store.select(fromRoot.getSpotlightContactsRequest).pipe(take(1))),
    switchMap((payload) => this.cs.getCustomersHttp(payload).pipe(
      map((res) => new care.SpotlightContactsSearchComplete((res))),
      catchError((error) => of(new care.SpotlightContactsSearchError(error)))
    ))
  )});

  public eligibleForRecovery$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(care.ActionTypes.SELECT_CUSTOMER),
    switchMap(() => this.store.select(fromRoot.getCareSelectedCustomer).pipe(take(1))),
    switchMap((payload) => this.cs.getEligibleForRecovery(payload).pipe(
      map((res) => new care.GetEligibleForRecoverySuccess(res)),
      catchError((error) => of(new care.GetEligibleForRecoveryError(error)))
    ))
  )});

  public getCustomerNotes$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(
      care.ActionTypes.SELECT_REPORT,
      care.ActionTypes.UPDATE_STATUS_SUCCESS,
      care.ActionTypes.LOAD_CASE_DETAILS_SUCCESS,
      care.ActionTypes.REOPEN_REPORT_SUCCESS,
      care.ActionTypes.CUSTOMER_NOTE_ACTION_COMPLETE,
      care.ActionTypes.DELETE_CUSTOMER_NOTE_SUCCESS
    ),
    switchMap(() => this.store.select(fromRoot.getSelectedCareReport).pipe(take(1))),
    filter((report) => report.cfaId != null),
    switchMap((report) => this.cs.getCustomerInformation(report).pipe(
      map((res) => new care.GetCustomerNotesSuccess(res)),
      catchError((error) => of(new care.GetCustomerNotesError(error)))
    ))
  )});

  public createCustomerNote: Observable<any> = createEffect(() => { return this.actions$.pipe(
    ofType(
      care.ActionTypes.CREATE_CUSTOMER_NOTE
    ),
    switchMap((action: any) =>
      of(action).pipe(
        concatLatestFrom(() =>
          this.store.select(fromRoot.getSelectedCareReport).pipe(take(1))
        ),
        switchMap(([action, report]) => {
          const noteData = action.payload;
          return this.cs.createCustomerNote(noteData, report.id).pipe(
            map((res) => new care.CustomerNoteActionComplete({ noteId: res.noteId })),
            catchError((err) => of(new care.CustomerNoteError({ err })))
          );
        })
      )
    )
  )});

  public updateCustomerNote: Observable<any> = createEffect(() => { return this.actions$.pipe(
    ofType(
      care.ActionTypes.UPDATE_CUSTOMER_NOTE
    ),
    switchMap((action: any) =>
      of(action).pipe(
        concatLatestFrom(() =>
          this.store.select(fromRoot.getSelectedCareReport).pipe(take(1))
        ),
        switchMap(([action, report]) => {
          let noteData = action.payload;
          return this.cs.updateCustomerNote(noteData, report.id).pipe(
            map((res) => new care.CustomerNoteActionComplete({ noteId: noteData.noteId })),
            catchError((err) => of(new care.CustomerNoteError({ err, noteId: noteData.noteId })))
          );
        })
      )
    )
  )});

  public deleteCustomerNote: Observable<any> = createEffect(() => { return this.actions$.pipe(
    ofType(
      care.ActionTypes.DELETE_CUSTOMER_NOTE
    ),
    map((action: any) => action.payload),
    switchMap((noteData) => {
      return this.cs.deleteCustomerNote(noteData).pipe(
        map((res) => new care.DeleteCustomerNoteSuccess({ noteId: noteData.noteId })),
        catchError((err) => of(new care.ErrorAction({ err, noteId: noteData.noteId })))
      );
    })
  )});

  public getRecoveredCfaOneContacts$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(
      care.ActionTypes.LOAD_RECOVERED_CFA_ONE_CONTACTS_DATA
    ),
    map((action: any) => action.payload),
    filter((payload) => payload && this.router.url.includes('/care')),
    switchMap(() => this.store.select(fromRoot.getRecoveredCfaOneContactsRequest).pipe(take(1))),
    switchMap((requestObj) => this.cs.getRecoveredCfaOneContacts(requestObj).pipe(
      map((res) => new care.GetRecoveredCfaOneContactsSuccess(res)),
      catchError((error) => of(new care.GetRecoveredCfaOneContactsError(error)))
    ))
  )});

  public getRecoveredCfaOneContactsOnAction$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(
      care.ActionTypes.RECOVERED_CFA_ONE_CONTACTS_CHANGE_PAGE_SIZE,
      care.ActionTypes.RECOVERED_CFA_ONE_CONTACTS_CHANGE_PAGE,
      care.ActionTypes.RECOVERED_CFA_ONE_CONTACTS_CHANGE_SORT,
      care.ActionTypes.RECOVERED_CFA_ONE_CONTACTS_CLEAR_SEARCH_TERM,
      care.ActionTypes.RECOVERED_CFA_ONE_CONTACTS_SEARCH,
      care.ActionTypes.RECOVERED_CFA_ONE_CONTACTS_SEARCH_BY,
      care.ActionTypes.RECOVERED_CFA_ONE_CHANGE_VIEW,
      user.ActionTypes.GET_LOCATION_DETAILS_SUCCESS
    ),
    filter(() => this.router.url.includes('/care')),
    debounceTime(200),
    switchMap(() => this.store.select(fromRoot.getRecoveredCfaOneContactsRequest).pipe(take(1))),
    switchMap((requestObj) => this.cs.getRecoveredCfaOneContacts(requestObj).pipe(
      map((res) => new care.GetRecoveredCfaOneContactsSuccess(res)),
      catchError((error) => of(new care.GetRecoveredCfaOneContactsError(error)))
    ))
  )});

  public createActionHistoryNote: Observable<any> = createEffect(() => { return this.actions$.pipe(
    ofType(care.ActionTypes.CREATE_ACTION_HISTORY_NOTE),
    switchMap((action: any) =>
      of(action).pipe(
        concatLatestFrom(() => this.store.select(fromRoot.getSelectedCareReport).pipe(take(1))),
        switchMap(([action]) => {
          let noteData = action.payload;
          return this.cs.createActionHistoryNote(noteData).pipe(
            map((res) => new care.CreateActionHistoryNoteSuccess(res)),
            catchError((err) => of(new care.CreateActionHistoryNoteError({ err })))
          );
        })
      )
    )
  )});

  public updateActionHistoryNote: Observable<any> = createEffect(() => { return this.actions$.pipe(
    ofType(care.ActionTypes.UPDATE_ACTION_HISTORY_NOTE),
    switchMap((action: any) =>
      of(action).pipe(
        concatLatestFrom(() => this.store.select(fromRoot.getSelectedCareReport).pipe(take(1))),
        switchMap(([action]) => {
          const noteData = action.payload;
          return this.cs.updateActionHistoryNote(noteData).pipe(
            map((res) => new care.UpdateActionHistoryNoteSuccess(res)),
            catchError((err) => of(new care.UpdateActionHistoryNoteError({ err, noteId: noteData.noteId })))
          );
        })
      )
    )
  )});

  constructor(
    private actions$: Actions,
    private cs: CareService,
    private dialog: MatDialog,
    private router: Router,
    private snackBar: MatSnackBar,
    private store: Store<fromRoot.State>
  ) { }
}
